import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { CheckCircleFilled, HistoryOutlined } from "@ant-design/icons";
import { SbTag, SbButton, SbTooltip } from "uiCore";
import {
  SuggestedRolesWrapperStyled,
  SuggestedRoleRowStyled,
} from "../../RedeploymentReductionDetailV2/RedeploymentReductionDetailV2.styles";
import { RowActionsStyled } from "../ViewPlanningV2.styles";
import NextRoleMatchTag from "../../components/NextRoleMatchTag";
import { useNextRolesForPerson } from "../../useTalentMobilityData";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";

const { Text } = Typography;

// One occupant row's expanded panel — their next best-matched roles from
// POST /talent-mobility/next-roles (fetched lazily on expand via
// useNextRolesForPerson, same as Reduction Detail's AtRiskDetailPanel), with
// a "Marked Available" tag on the roles the saved plan targeted for this
// person. Read-only: no Mark as Available here, the plan is already saved.
const PlanNextRolesPanel = ({ person, targetedRoleIds }) => {
  const thresholds = useMatchThresholds();
  const navigate = useNavigate();
  const { nextRoles, isLoading } = useNextRolesForPerson(person);
  const suggestedRoles = useMemo(() => nextRoles.slice(0, 3), [nextRoles]);

  return (
    <SuggestedRolesWrapperStyled>
      <div className="nested-title">Next best-matched roles for {person.name}</div>
      {isLoading ? (
        <Text type="secondary">Loading next roles…</Text>
      ) : suggestedRoles.length ? (
        suggestedRoles.map((m) => (
          <SuggestedRoleRowStyled key={m.roleId}>
            <span>
              {m.role?.isPast && (
                <SbTooltip content="This employee previously held this role — the assignment has ended.">
                  <SbTag color="gold" icon={<HistoryOutlined />} style={{ marginRight: 6, fontWeight: 600 }}>
                    Past Role
                  </SbTag>
                </SbTooltip>
              )}
              {m.role?.title}
              {targetedRoleIds.has(m.roleId) && (
                <SbTag color="success" icon={<CheckCircleFilled />} style={{ marginLeft: 6 }}>
                  Marked Available
                </SbTag>
              )}
            </span>
            <RowActionsStyled>
              <NextRoleMatchTag sri={m.sri} thresholds={thresholds} />
              <SbButton
                buttonType="secondary"
                size="small"
                onClick={() =>
                  // flow=view-planning tells the Candidate Profile this is a
                  // read-only visit — no Shortlist/availability header action.
                  navigate(`/talent-mobility/candidate/${person.id}/role/${m.roleId}?index=smi&flow=view-planning`)
                }
              >
                View Profile
              </SbButton>
            </RowActionsStyled>
          </SuggestedRoleRowStyled>
        ))
      ) : (
        <Text type="secondary">No matched next roles for this person yet.</Text>
      )}
    </SuggestedRolesWrapperStyled>
  );
};

export default PlanNextRolesPanel;
